import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import type { ProcessedDimension } from './types'

const ENABLED_ALPHA = '0.9'

const COLOR_MAP: Record<string, string> = {
  blue: `rgba(59, 130, 246, ${ENABLED_ALPHA})`,
  red: `rgba(239, 68, 68, ${ENABLED_ALPHA})`,
  green: `rgba(34, 197, 94, ${ENABLED_ALPHA})`,
  yellow: `rgba(234, 179, 8, ${ENABLED_ALPHA})`,
  purple: `rgba(168, 85, 247, ${ENABLED_ALPHA})`,
  indigo: `rgba(79, 70, 229, ${ENABLED_ALPHA})`,
  gray: `rgba(107, 114, 128, ${ENABLED_ALPHA})`,
}

function hexToRgb(hex: string): string {
  hex = hex.replace(/^#/, '')
  let r: number, g: number, b: number
  if (hex.length === 3) {
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16)
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16)
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16)
  } else {
    r = parseInt(hex.substring(0, 2), 16)
    g = parseInt(hex.substring(2, 4), 16)
    b = parseInt(hex.substring(4, 6), 16)
  }
  return `${r}, ${g}, ${b}`
}

function getChartColor(color: string, id: string): string {
  if (color && color.startsWith('#')) {
    return `rgba(${hexToRgb(color)}, ${ENABLED_ALPHA})`
  }
  if (!color || typeof color !== 'string') {
    return COLOR_MAP.gray
  }
  if (color === 'blue' && typeof id === 'string') {
    if (id === 'radar') return COLOR_MAP.blue
    if (id === 'features') return COLOR_MAP.green
  }
  return COLOR_MAP[color] || COLOR_MAP.blue
}

export function useTimelineData(props: { dimensions: any[] }) {
  const store = useStore()
  const datasetVisibility = ref(new Map<string, boolean>())

  const processedDimensions = computed<ProcessedDimension[]>(() => {
    const dates = store.getters['layers/getFilteredDates'] || []
    let allDimensions = Array.isArray(props.dimensions) ? [...props.dimensions] : []
    let processedStoreDates: any[] = []

    if (Array.isArray(dates)) {
      dates.forEach((date: any) => {
        if (date && date.layers && Array.isArray(date.layers)) {
          processedStoreDates.push(...date.layers)
        } else {
          processedStoreDates.push(date)
        }
      })
      allDimensions = [...allDimensions, ...processedStoreDates]
    }

    return allDimensions
      .map((dimension) => {
        if (!dimension || typeof dimension !== 'object') return null

        const id = dimension.id || 'unknown'
        const name = dimension.name || dimension.id || 'Unknown'
        const color = dimension.color || 'blue'
        const chartColor = getChartColor(color, id)

        let rawDates: any[] = []
        if (
          dimension.dates &&
          typeof dimension.dates === 'object' &&
          !Array.isArray(dimension.dates) &&
          Array.isArray(dimension.dates.dates)
        ) {
          rawDates = dimension.dates.dates
        } else if (Array.isArray(dimension.dates)) {
          rawDates = dimension.dates
        }

        return { id, name, color, chartColor, rawDates }
      })
      .filter((dim): dim is ProcessedDimension => dim !== null && dim.id !== undefined)
  })

  const allDates = computed(() => {
    const dateSet = new Set<number>()
    processedDimensions.value.forEach((dim) => {
      dim.rawDates.forEach((item: any) => {
        if (item.startDate && item.endDate) {
          const s = parseDate(item.startDate)
          const e = parseDate(item.endDate)
          if (s) dateSet.add(s)
          if (e) dateSet.add(e)
        } else if (item.date) {
          const d = parseDate(item.date)
          if (d) dateSet.add(d)
        }
      })
    })
    return Array.from(dateSet).sort((a, b) => a - b)
  })

  const hasTimeline = computed(() => allDates.value.length > 0)

  const visibleDimensions = computed(() => {
    return processedDimensions.value.filter(
      (dim) => datasetVisibility.value.get(dim.id) !== false
    )
  })

  const singleGroup = [{ id: 'chart', label: '' }]

  const allDatasets = computed(() => {
    return processedDimensions.value.map((dim) => ({
      label: dim.id,
      displayName: dim.name,
      backgroundColor: dim.chartColor,
    }))
  })

  function toggleDatasetVisibility(datasetLabel: string) {
    const current = datasetVisibility.value.get(datasetLabel)
    const newMap = new Map(datasetVisibility.value)
    newMap.set(datasetLabel, current === false ? true : false)
    datasetVisibility.value = newMap
  }

  return {
    processedDimensions,
    visibleDimensions,
    allDates,
    hasTimeline,
    singleGroup,
    allDatasets,
    datasetVisibility,
    toggleDatasetVisibility,
  }
}

function parseDate(dateStr: string): number | null {
  if (!dateStr) return null
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(dateStr)
  const d = new Date(hasTimezone ? dateStr : dateStr + 'Z')
  return isNaN(d.getTime()) ? null : d.getTime()
}
